import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { closeIcon, searchIcon } from '../../utils/images';
import { Pagination } from '../Pagination';
import { usePersistentImage } from '../../hook/usePersistentImage';
import { listUserMedia, uploadUserMedia } from '../../services/personalMediaList';
import type { PersonalMediaResponse } from '../../services/personalMediaList';
import { getDisplaySettings, updateDisplaySettings } from '../../services/bigDisplayData';
import {PlaybackSettings, DEFAULT_PLAYBACK_SETTINGS, MIN_PLAYBACK_INTERVAL_MINUTES,} from '../../types/BigDisplay';
import { Media } from '../../types/Media';
import { useAuth } from '../../contexts/AuthContext';
import { usePersonalMediaLibrary } from '../../contexts/PersonalMediaLibraryContext';
import '../MediaLibraryPopup/MediaLibraryPopup.scss';

interface PersonalMediaLibraryPopupProps {
  onClose: () => void;
  onMediaSelect?: (mediaUrls: string[]) => void;
  isUploading?: boolean;
  displayId?: number;
  onStatus?: (status: 'loading' | 'success' | 'error', message: string) => void;
  maxSelection?: number;
}

const PAGE_SIZE = 20;

interface PersonalMediaPopupItemProps {
  image: Media;
  isSelected: boolean;
  isUploading: boolean;
  onClick: (mediaUrl: string) => void;
}

const PersonalMediaPopupItem: React.FC<PersonalMediaPopupItemProps> = ({
  image,
  isSelected,
  isUploading,
  onClick,
}) => {
  const { imageUrl, loading, error } = usePersistentImage({
    url: image.url,
    lastModified: image.last_modified,
  });

  return (
    <div
      className={`media-popup__item${isUploading ? ' media-popup__item--disabled' : ''}${isSelected ? ' media-popup__item--selected' : ''}`}
      onClick={() => onClick(image.url)}
    >
      {loading ? (
        <div className="media-popup__image-loading">Loading...</div>
      ) : error || !imageUrl ? (
        <div className="media-popup__image-error">Failed to load</div>
      ) : (
        <img
          src={imageUrl}
          alt={image.name}
          className="media-popup__image"
        />
      )}
      {isSelected && <div className="media-popup__item-check">✓</div>}
    </div>
  );
};

export const PersonalMediaLibraryPopup: React.FC<PersonalMediaLibraryPopupProps> = ({
  onClose,
  onMediaSelect,
  isUploading = false,
  displayId,
  onStatus,
  maxSelection,
}) => {
  const [activeTab, setActiveTab] = useState<'library' | 'settings'>('library');
  const [draft, setDraft] = useState<PlaybackSettings>(DEFAULT_PLAYBACK_SETTINGS);
  const [intervalDraftMinutes, setIntervalDraftMinutes] = useState(MIN_PLAYBACK_INTERVAL_MINUTES);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showUploadPanel, setShowUploadPanel] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const [previewErrors, setPreviewErrors] = useState<Record<number, boolean>>({});
  const [uploadPending, setUploadPending] = useState(false);
  const [selectedUrls, setSelectedUrls] = useState<string[]>([]);
  const intervalDraftIsInvalid = !Number.isFinite(intervalDraftMinutes) || intervalDraftMinutes < MIN_PLAYBACK_INTERVAL_MINUTES;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { session, loading: authLoading } = useAuth();
  const { refresh: refreshSharedMediaLibrary } = usePersonalMediaLibrary();
  const [mediaData, setMediaData] = useState<PersonalMediaResponse>({
    limit: 0,
    offset: 0,
    total: 0,
    images: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(mediaData.total / PAGE_SIZE));
  const selectionLimit = maxSelection ?? Number.POSITIVE_INFINITY;
  const hasSelectionLimit = Number.isFinite(selectionLimit);

  const fetchMedia = useCallback(async (page: number) => {
    if (authLoading) {
      setLoading(true);
      return;
    }

    if (!session?.access_token) {
      setMediaData({ limit: 0, offset: 0, total: 0, images: [] });
      setError(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const offset = (page - 1) * PAGE_SIZE;
      const data = await listUserMedia(PAGE_SIZE, offset);
      setMediaData(data);
    } catch {
      setError('Failed to load your media library');
    } finally {
      setLoading(false);
    }
  }, [authLoading, session?.access_token]);

  useEffect(() => {
    fetchMedia(currentPage);
  }, [currentPage, fetchMedia]);

  const refresh = async () => {
    await fetchMedia(currentPage);
  };

  const goToPage = (page: number) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  useEffect(() => {
    if (!displayId) return;
    getDisplaySettings(displayId)
      .then(settings => {
        setDraft(settings);
        setIntervalDraftMinutes(Math.max(MIN_PLAYBACK_INTERVAL_MINUTES, Math.ceil(settings.interval / 60)));
      })
      .catch(() => {});
  }, [displayId]);

  const saveSettings = async () => {
    if (!displayId) return;
    if (intervalDraftIsInvalid) {
      onStatus?.('error', `Interval must be at least ${MIN_PLAYBACK_INTERVAL_MINUTES} minutes`);
      return;
    }

    setSettingsSaving(true);
    onStatus?.('loading', 'Saving display settings...');
    try {
      const updated = await updateDisplaySettings(displayId, {
        ...draft,
        interval: intervalDraftMinutes * 60,
      });
      setDraft(updated);
      setIntervalDraftMinutes(Math.max(MIN_PLAYBACK_INTERVAL_MINUTES, Math.ceil(updated.interval / 60)));
      onStatus?.('success', 'Display settings saved');
    } catch {
      onStatus?.('error', 'Failed to save display settings');
    } finally {
      setSettingsSaving(false);
    }
  };

  const filteredMedia = useMemo(() =>
    mediaData.images.filter(img => img.name.toLowerCase().includes(searchQuery.toLowerCase())),
    [mediaData.images, searchQuery]
  );

  const handleMediaClick = (mediaUrl: string) => {
    if (isUploading) return;
    setSelectedUrls(prev => {
      if (prev.includes(mediaUrl)) {
        return prev.filter(u => u !== mediaUrl);
      }

      if (prev.length >= selectionLimit) {
        onStatus?.('error', `You can send up to ${selectionLimit} images at once`);
        return prev;
      }

      return [...prev, mediaUrl];
    });
  };

  const handleSendSelected = () => {
    if (selectedUrls.length === 0) return;
    if (selectedUrls.length > selectionLimit) {
      onStatus?.('error', `You can send up to ${selectionLimit} images at once`);
      return;
    }

    if (onMediaSelect) {
      onMediaSelect(selectedUrls);
    }
  };

  const handleUploadButtonClick = () => {
    setShowUploadPanel(true);
  };

  const handleBack = () => {
    setShowUploadPanel(false);
    setPendingFiles([]);
    setPreviewUrls([]);
    setPreviewErrors({});
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const incomingFiles = Array.from(e.target.files ?? []);
    const files = incomingFiles.slice(0, selectionLimit);
    if (files.length === 0) return;
    if (incomingFiles.length > selectionLimit) {
      onStatus?.('error', `You can upload and send up to ${selectionLimit} images at once`);
    }

    setPendingFiles(files);
    setPreviewErrors({});
    Promise.all(
      files.map(
        file =>
          new Promise<string>(resolve => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.readAsDataURL(file);
          })
      )
    ).then(urls => setPreviewUrls(urls));
  };

  const handleUploadConfirm = async () => {
    if (pendingFiles.length === 0) return;
    if (pendingFiles.length > selectionLimit) {
      onStatus?.('error', `You can upload and send up to ${selectionLimit} images at once`);
      return;
    }

    setUploadPending(true);
    try {
      const result = await uploadUserMedia(pendingFiles);
      await refreshSharedMediaLibrary();
      const fileUrls = result.uploaded_files.map(f => f.file_url);
      if (fileUrls.length > 0 && onMediaSelect) {
        onMediaSelect(fileUrls);
      }
    } catch {
      alert('Upload failed. Please try again.');
    } finally {
      setUploadPending(false);
    }
  };

  if (error) {
    return (
      <div className="media-popup">
        <div className="media-popup__header">
          <div className="media-popup__close" onClick={onClose}>
            <img className="media-popup__close--icon" src={closeIcon} alt="close" />
          </div>
          <h1 className="media-popup__title">My Media</h1>
          <button className="media-popup__upload-btn" onClick={handleUploadButtonClick}>Upload</button>
        </div>
        <div className="media-popup__container">
          <div className="media-popup__error">
            <p>Error: {error}</p>
            <button onClick={refresh}>Retry</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="media-popup">
      <div className="media-popup__header">
        <div className="media-popup__close" onClick={onClose}>
          <img className="media-popup__close--icon" src={closeIcon} alt="close" />
        </div>
        <h1 className="media-popup__title">My Media</h1>
        <button className="media-popup__upload-btn" onClick={handleUploadButtonClick} disabled={isUploading}>
          Upload
        </button>
      </div>

      {displayId && (
        <div className="media-popup__tabs">
          <button
            className={`media-popup__tab${activeTab === 'library' ? ' media-popup__tab--active' : ''}`}
            onClick={() => setActiveTab('library')}
          >
            Library
          </button>
          <button
            className={`media-popup__tab${activeTab === 'settings' ? ' media-popup__tab--active' : ''}`}
            onClick={() => setActiveTab('settings')}
          >
            Settings
          </button>
        </div>
      )}

      <div className="media-popup__container">
        {activeTab === 'settings' && displayId ? (
          <div className="media-popup__settings">
            <div className="media-popup__settings-row">
              <label className="media-popup__settings-label">Interval (minutes)</label>
              <input
                className="media-popup__settings-input"
                type="number"
                min={MIN_PLAYBACK_INTERVAL_MINUTES}
                step={1}
                value={intervalDraftMinutes}
                aria-invalid={intervalDraftIsInvalid}
                aria-describedby="media-popup-interval-error"
                onChange={e => {
                  const nextValue = Number(e.target.value);
                  if (!Number.isNaN(nextValue)) {
                    setIntervalDraftMinutes(nextValue);
                  }
                }}
              />
            </div>
            {intervalDraftIsInvalid && (
              <p id="media-popup-interval-error" className="media-popup__settings-error">
                Minimum interval is {MIN_PLAYBACK_INTERVAL_MINUTES} minutes.
              </p>
            )}
            <div className="media-popup__settings-row">
              <label className="media-popup__settings-label">Shuffle</label>
              <button
                className={`media-popup__settings-toggle${draft.shuffle ? ' media-popup__settings-toggle--on' : ''}`}
                onClick={() => setDraft(d => ({ ...d, shuffle: !d.shuffle }))}
              >
                {draft.shuffle ? 'On' : 'Off'}
              </button>
            </div>
            <div className="media-popup__settings-row">
              <label className="media-popup__settings-label">Repeat</label>
              <button
                className={`media-popup__settings-toggle${draft.repeat ? ' media-popup__settings-toggle--on' : ''}`}
                onClick={() => setDraft(d => ({ ...d, repeat: !d.repeat }))}
              >
                {draft.repeat ? 'On' : 'Off'}
              </button>
            </div>
            <div className="media-popup__settings-row">
              <label className="media-popup__settings-label">Paused</label>
              <button
                className={`media-popup__settings-toggle${draft.paused ? ' media-popup__settings-toggle--on' : ''}`}
                onClick={() => setDraft(d => ({ ...d, paused: !d.paused }))}
              >
                {draft.paused ? 'Paused' : 'Playing'}
              </button>
            </div>
            <div className="media-popup__settings-actions">
              <button
                className="media-popup__settings-save"
                onClick={saveSettings}
                disabled={settingsSaving || intervalDraftIsInvalid}
              >
                {settingsSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        ) : showUploadPanel ? (
          <div className="media-popup__upload-panel">
            <div
              className="media-popup__upload-preview-area"
              onClick={() => !uploadPending && !isUploading && fileInputRef.current?.click()}
            >
              {previewUrls.length > 0 ? (
                <div className="media-popup__upload-preview-grid">
                  {previewUrls.map((url, i) => (
                    previewErrors[i] ? (
                      <div key={i} className="media-popup__upload-preview-fallback">
                        <span className="media-popup__upload-preview-filename">{pendingFiles[i]?.name || `Image ${i + 1}`}</span>
                      </div>
                    ) : (
                      <img
                        key={i}
                        src={url}
                        alt={pendingFiles[i]?.name || `Image ${i + 1}`}
                        className="media-popup__upload-preview-thumb"
                        onError={() => setPreviewErrors(prev => ({ ...prev, [i]: true }))}
                      />
                    )
                  ))}
                </div>
              ) : (
                <span className="media-popup__upload-placeholder">Click to select images</span>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
            <div className="media-popup__upload-actions">
              <button
                className="media-popup__upload-back-btn"
                onClick={handleBack}
                disabled={uploadPending || isUploading}
              >
                Back
              </button>
              <button
                className="media-popup__upload-confirm-btn"
                onClick={handleUploadConfirm}
                disabled={pendingFiles.length === 0 || uploadPending || isUploading}
              >
                {isUploading ? 'Sending to display...' : uploadPending ? 'Uploading...' : `Confirm${pendingFiles.length > 1 ? ` (${pendingFiles.length})` : ''}`}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="media-popup__search">
              <img className="media-popup__search--icon" src={searchIcon} alt="search" />
              <input
                type="text"
                className="media-popup__search--input"
                placeholder="Search media files"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
            </div>

            {loading && mediaData.images.length === 0 ? (
              <div className="media-popup__loading">Loading media...</div>
            ) : (
              <div className="media-popup__list">
                {isUploading && (
                  <div className="media-popup__upload-overlay">
                    <div className="media-popup__upload-message">Uploading image to display...</div>
                  </div>
                )}
                {filteredMedia.length > 0 ? (
                  filteredMedia.map(img => {
                    const isSelected = selectedUrls.includes(img.url);
                    return (
                      <PersonalMediaPopupItem
                        key={img.url}
                        image={img}
                        isSelected={isSelected}
                        isUploading={isUploading}
                        onClick={handleMediaClick}
                      />
                    );
                  })
                ) : (
                  <div className="media-popup__no-results">No media files found.</div>
                )}
              </div>
            )}
            <div className="media-popup__pagination">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={goToPage}
                disabled={loading || isUploading}
              />
            </div>
            {selectedUrls.length > 0 && (
              <div className="media-popup__send-bar">
                <span className="media-popup__send-count">
                  {selectedUrls.length} selected{hasSelectionLimit ? ` / ${selectionLimit}` : ''}
                </span>
                <button
                  className="media-popup__send-btn"
                  onClick={handleSendSelected}
                  disabled={isUploading}
                >
                  Send to Display
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
